import React, { useState, useRef } from 'react';

interface ProjectSummaryProps {
  budgetTotal: string;
  surfaceTraitee: string;
  informationsProjet?: string;
  onBudgetChange: (budget: string) => void;
  onSurfaceChange: (surface: string) => void;
  onInformationsChange?: (infos: string) => void;
}

// ── formatage français ────────────────────────────────────────────────────────

function parseNum(raw: string): number | null {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

function formatFR(raw: string, decimals = 0): string | null {
  const n = parseNum(raw);
  if (n === null) return null;
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(n);
}

// ── composant champ stat inline ───────────────────────────────────────────────

interface StatFieldProps {
  label: string;
  value: string;
  unit: string;
  placeholder: string;
  inputType?: string;
  decimals?: number;
  onCommit: (v: string) => void;
  /** Ref vers l'input précédent pour Shift+Tab personnalisé (non requis, Tab natif suffit) */
  nextRef?: React.RefObject<HTMLInputElement | null>;
}

const StatField: React.FC<StatFieldProps> = ({
  label,
  value,
  unit,
  placeholder,
  decimals = 0,
  onCommit,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isEmpty = value === '' || value === '0';
  const formatted = isEmpty ? null : formatFR(value, decimals);

  const startEdit = () => {
    const n = parseNum(value);
    setDraft(n !== null && !isEmpty ? String(n) : '');
    setEditing(true);
    // autoFocus via ref après le re-render
    setTimeout(() => { inputRef.current?.select(); }, 0);
  };

  const commit = () => {
    const n = parseNum(draft);
    onCommit(n !== null ? String(n) : '');
    setEditing(false);
  };

  const cancel = () => setEditing(false);

  if (editing) {
    return (
      <div className="flex items-baseline gap-1">
        <span className="text-xs text-gray-400 mr-0.5">{label}</span>
        <input
          ref={inputRef}
          type="number"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          autoFocus
          placeholder="0"
          className="w-24 text-sm font-semibold text-gray-900 bg-violet-50 border-0 border-b-2 border-violet-400 outline-none rounded-sm px-1 py-0 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <span className="text-xs text-gray-400">{unit}</span>
      </div>
    );
  }

  return (
    <button
      onClick={startEdit}
      className="group flex items-baseline gap-1 hover:bg-gray-50 rounded-md px-2 py-1 -mx-2 -my-1 transition-colors"
      title={`Modifier ${label.toLowerCase()}`}
    >
      <span className="text-xs text-gray-400 group-hover:text-gray-500 transition-colors">{label}</span>
      {formatted ? (
        <>
          <span className="text-sm font-semibold text-gray-800 group-hover:text-violet-700 transition-colors tabular-nums">
            {formatted}
          </span>
          <span className="text-xs text-gray-500">{unit}</span>
        </>
      ) : (
        <span className="text-sm text-gray-300 italic">{placeholder}</span>
      )}
    </button>
  );
};

// ── ProjectSummary ─────────────────────────────────────────────────────────────

export const ProjectSummary: React.FC<ProjectSummaryProps> = ({
  budgetTotal,
  surfaceTraitee,
  onBudgetChange,
  onSurfaceChange,
}) => {
  // Ratio €/m² — affiché uniquement quand les deux sont renseignés
  const surfaceNum = parseNum(surfaceTraitee);
  const budgetNum = parseNum(budgetTotal);
  const ratio =
    surfaceNum && surfaceNum > 0 && budgetNum && budgetNum > 0
      ? budgetNum / surfaceNum
      : null;

  return (
    <div className="flex items-center gap-6 flex-wrap">
      <StatField
        label="Surface"
        value={surfaceTraitee}
        unit="m²"
        placeholder="— m²"
        onCommit={onSurfaceChange}
      />
      <StatField
        label="Budget"
        value={budgetTotal}
        unit="€"
        placeholder="— €"
        onCommit={onBudgetChange}
      />
      {ratio !== null && (
        <span className="text-xs text-gray-400 tabular-nums">
          ≈ {new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(ratio)} €/m²
        </span>
      )}
    </div>
  );
};
